import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Calendar, ArrowRight } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Placeholder blog posts
  const placeholderPosts = [
    {
      id: "1",
      title: "The Art of Being Present",
      slug: "art-of-being-present",
      excerpt: "In our hyperconnected world, true presence has become the rarest luxury. Here's what it means to be fully there...",
      content: "",
      featured_image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600",
      published: true,
      created_at: "2025-01-10T12:00:00Z"
    },
    {
      id: "2",
      title: "Travel Diary: A Week in Paris",
      slug: "travel-diary-paris",
      excerpt: "From hidden cafés to moonlit walks along the Seine, my favorite moments from the City of Light...",
      content: "",
      featured_image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600",
      published: true,
      created_at: "2025-01-05T12:00:00Z"
    },
    {
      id: "3",
      title: "What Makes a True Gentleman",
      slug: "what-makes-a-true-gentleman",
      excerpt: "Reflections on the qualities that distinguish truly remarkable men from the merely successful...",
      content: "",
      featured_image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600",
      published: true,
      created_at: "2024-12-28T12:00:00Z"
    },
    {
      id: "4",
      title: "Self-Care Rituals I Swear By",
      slug: "self-care-rituals",
      excerpt: "The daily practices that keep me feeling radiant, centered, and ready for whatever adventure awaits...",
      content: "",
      featured_image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600",
      published: true,
      created_at: "2024-12-20T12:00:00Z"
    },
  ];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/public/blog`);
      if (response.data && response.data.length > 0) {
        setPosts(response.data);
      } else {
        setPosts(placeholderPosts);
      }
    } catch (error) {
      setPosts(placeholderPosts);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div data-testid="blog-page">
      {/* Header */}
      <section className="page-header" data-testid="blog-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="blog.thoughts-musings" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Thoughts & Musings</T>
          <T k="blog.blog" as="h1" className="page-title animate-fade-in stagger-1">Blog</T>
          <T k="blog.personal-reflections-on" as="p" className="page-subtitle animate-fade-in stagger-2">Personal reflections on life, travel, connection, and the pursuit of beautiful experiences.</T>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24" data-testid="blog-grid">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="blog-card group"
                  data-testid={`blog-post-${index}`}
                >
                  {post.featured_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <div className="flex items-center gap-2 text-taupe mb-4">
                      <Calendar className="w-4 h-4" />
                      <span className="font-body text-sm">{formatDate(post.created_at)}</span>
                    </div>
                    <h2 className="font-heading text-2xl text-charcoal mb-3 group-hover:text-jade-text transition-colors">
                      {post.title}
                    </h2>
                    <p className="font-body text-stone-grey leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <span className="font-body text-sm text-jade-text flex items-center gap-2 group-hover:text-champagne-gold transition-colors">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-jade-deep">
        <div className="max-w-4xl mx-auto text-center">
          <T k="blog.never-miss-a" as="h2" className="font-heading text-3xl text-ivory mb-6">Never Miss a Post</T>
          <T k="blog.subscribe-to-my" as="p" className="font-body text-ivory/80 mb-8">Subscribe to my newsletter and receive new articles directly to your inbox.</T>
          <T k="blog.subscribe-now" as="a" href="/#newsletter-section" className="bg-champagne-gold text-charcoal px-8 py-3 rounded-full font-body text-sm tracking-widest uppercase hover:bg-champagne transition-all duration-300 inline-block">Subscribe Now</T>
        </div>
      </section>
    </div>
  );
};

export default Blog;
